import Controller, { OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../../../BaseController';
import { HttpService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ActionAPI, CheckboxAPI, FieldAPI, FormAPI, InputAPI, ModalProps, ModalService, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { FragmentNames, ScorePewsViewerAliases } from '../../../../codegen/index';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { ApplicationRoute } from '../../../../constants/routes';

import { ScorePEWSResponse } from '../../score-types';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { Value } from '@philips-internal/oswald-ui-core';
import { ScoreAndIndexesService, ScoreListContext } from '../../scores-list/scores-list-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { HealthProfessionalUser } from '../../../../app/authentication';

@Controller({ fragment: FragmentNames.SCORE_PEWS_VIEWER })
export default class ScorePewsViewerController extends BaseController {

  @Inject(ScorePewsViewerAliases.FORM_SCORE_PEWS_VIEWER)
  private formAPI!: FormAPI;

  @Inject(ScorePewsViewerAliases.INPUT_EVATUALION_DATE)
  private inputDateAPI!: InputAPI;

  @Inject(ScorePewsViewerAliases.TEXT_PROFESSIONAL)
  private textProfessionalAPI!: TextAPI;

  @Inject(ScorePewsViewerAliases.TEXT_NEUROLOGICAL_STATUS)
  private textNeurologicalAPI!: TextAPI;

  @Inject(ScorePewsViewerAliases.TEXT_CARDIOVASCULAR)
  private textCardiovascularAPI!: TextAPI;

  @Inject(ScorePewsViewerAliases.TEXT_RESPIRATORY)
  private textRespiratoryAPI!: TextAPI;

  @Inject(ScorePewsViewerAliases.TEXT_PUNCTUATION)
  private textPunctuationAPI!: TextAPI;

  @Inject(ScorePewsViewerAliases.TEXT_RESULT)
  private textResultAPI!: TextAPI;

  @Inject(ScorePewsViewerAliases.CHECKBOX_NEBULIZATION)
  private checkboxNebulizationAPI!: CheckboxAPI;

  @Inject(ScorePewsViewerAliases.CHECKBOX_VOMIT)
  private checkboxVomitAPI!: CheckboxAPI;

  @Inject(ScorePewsViewerAliases.FIELD_CARDIOVASCULAR)
  private fieldCardiovascularAPI!: FieldAPI;

  @Inject(ScorePewsViewerAliases.FIELD_NEUROLOGICAL)
  private fieldNeurologicalAPI!: FieldAPI;

  @Inject(ScorePewsViewerAliases.FIELD_RESPIRATORY)
  private fieldRespiratoryAPI!: FieldAPI;

  @Inject(ScorePewsViewerAliases.ACTION_INACTIVATE_SCORE_PEWS)
  private actionInactivateAPI!: ActionAPI;

  @Inject(ScorePewsViewerAliases.ACTION_NEW_SCORE_PEWS)
  private actionNewAPI!: ActionAPI;

  @Inject(ScorePewsViewerAliases.ACTION_REFERENCE_SCORE_PEWS)
  private actionReferenceAPI!: ActionAPI;

  @OnMount({ target: ScorePewsViewerAliases.LAYOUT_SCORE_PEWS_VIEWER })
  handleMountLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    applicationContext.popNavigationItem();
    this.context.set({encounterId, patientId});
    ScoreListContext.currentTab = ScoreType.SCORE_PEWS
  }

  private handleVisibleFields(record: ScorePEWSResponse): void {
    if (!record.neurologicalStatus) {
      this.fieldNeurologicalAPI.setVisible(false);
    }
    if (!record.cardiovascular) {
      this.fieldCardiovascularAPI.setVisible(false);
    }
    if (!record.respiratory) {
      this.fieldRespiratoryAPI.setVisible(false);
    }
  }

  @OnMount({ target: ScorePewsViewerAliases.FORM_SCORE_PEWS_VIEWER })
  handleMountForm(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    this.formAPI.setLoading(true);
    this.loadEditionData(idScoreIndexes as number);
  }

  private loadEditionData(sequenceNumber: number): void {
    const allowInfo = InformationBarUtils.allowInfo && ScoreAndIndexesService.isAllowEdit;

    HttpService.get(Endpoint.SCORE_PEWS, { sequenceNumber })
      .then(response => {
        const record = response.body as ScorePEWSResponse;
        const user = foundationContext.getUser() as HealthProfessionalUser;
        const date = record.dateEvaluation || '';
        const professional = record.professionalName || '';
        const neuro = record.neurologicalStatus ? record.neurologicalStatus.description : '';
        const cardio = record.cardiovascular ? record.cardiovascular.description : '';
        const respiratory = record.respiratory ? record.respiratory.description : '';
        const result = record.result as unknown as string || '';
        const score = record.scoreQuantity as unknown as string || '0';
        const showInactivate = record.professionalCode === user.physicalPersonId && Boolean(record.showInactivate) && allowInfo;

        this.inputDateAPI.setValue(DateFormatterUtil.timeStamp(String(date)));
        this.textProfessionalAPI.setValue(professional);
        this.textNeurologicalAPI.setValue(neuro);
        this.textCardiovascularAPI.setValue(cardio);
        this.textRespiratoryAPI.setValue(respiratory);
        this.checkboxNebulizationAPI.setValue(BooleanUtils.isTrueValue(record.nebulization));
        this.checkboxVomitAPI.setValue(BooleanUtils.isTrueValue(record.vomit));
        this.textPunctuationAPI.setValue(score);
        this.textResultAPI.setValue(result);
        this.actionInactivateAPI.setEnabled(showInactivate);

        this.handleVisibleFields(record);
      }).catch(err => this.showToast(err?.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formAPI.setLoading(false);
        this.actionNewAPI.setEnabled(allowInfo);
        this.actionReferenceAPI.setEnabled(allowInfo);
      });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-pews-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private getReference(): void {
    const scoreType = 'pews';
    const modalProperties: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-reference',
      id: 'score-reference',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'tws.reference-pain-scale-value',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      scoreType,
    }

    ModalService.openFullscreen(modalProperties);
  }

  @OnClick({ target: ScorePewsViewerAliases.ACTION_NEW_SCORE_PEWS })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.SCORE_PEWS_CREATE, {
      encounterId,
      patientId,
    });
  }

  @OnClick({ target: ScorePewsViewerAliases.ACTION_INACTIVATE_SCORE_PEWS})
  handleClickInactivate(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-inactivate',
      id: 'score-inactivate',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      idScoreIndexes,
    } as Record<string, Value>

    ModalService.openDefault(modalProperties);
  }

  @OnClick({ target: ScorePewsViewerAliases.ACTION_REFERENCE_SCORE_PEWS })
  handleClickReference(): void {
    this.getReference();
  }

  @OnUnmount(ScorePewsViewerAliases.LAYOUT_SCORE_PEWS_VIEWER)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_PEWS;
  }
}
